import posthog, { type PostHog } from 'posthog-js';
import { readCrossFrameworkEnv } from './env';
import { DEFAULT_POSTHOG_HOST, POSTHOG_QA_PROJECT_KEY } from './posthogConstants';
import { analyticsEnvironment } from './env';
import { REPLAY_BLOCK_SELECTOR, maskReplayText } from '../observability/replayMasking';
import { registerTenantContext } from './tenantContextRegistrar';
import type { PosthogCommunityConfig } from './types';

let activeClient: PostHog | null = null;
let instanceCounter = 0;
let latestSwitchOp = 0;

export function getActivePosthogClient(): PostHog | null {
	return activeClient;
}

/**
 * reset() only when the client carries an identified user. On an anonymous visitor reset()
 * would mint a new distinct_id and cut them off from the person that visited www.networked.co
 * (the ph_* cookie is shared on .networked.co), which is the funnel break Spencer reported.
 */
export function resetIfIdentified(client: PostHog | null): void {
	if (!client) {
		return;
	}
	try {
		if (client.get_property('$user_state') === 'identified') {
			client.reset();
		}
	} catch {
		// ignore
	}
}

function disposeClient(client: PostHog | null): void {
	resetIfIdentified(client);
}

const POSTHOG_COOKIE = /^ph_(.+)_posthog$/;

/**
 * Networked's own project: the one the website uses too. Only it keeps a cookie, which
 * posthog-js shares on .networked.co so a website visitor is the same person after sign-up.
 */
const isNetworkedProject = (apiKey: string): boolean =>
	apiKey === (readCrossFrameworkEnv('POSTHOG_KEY') || '').trim();

/**
 * Deletes every community project's `ph_<key>_posthog` cookie, on this host and on the parent
 * domain posthog-js writes them to. With one project per community, an owner who visited many
 * communities carried one such cookie each, until nginx refused the request outright ("400
 * Request Header Or Cookie Too Large"). Those projects now keep their state in localStorage
 * (see `persistence` below), which already holds the same ids, so nothing is lost.
 */
export function clearCommunityPosthogCookies(keepApiKey: string | undefined): void {
	if (typeof document === 'undefined') return;
	const host = window.location?.hostname || '';
	const labels = host.split('.');
	const domains = host ? [host, `.${host}`, ...(labels.length > 2 ? [`.${labels.slice(-2).join('.')}`] : [])] : [];
	for (const pair of document.cookie.split(';')) {
		const name = pair.split('=')[0].trim();
		const match = POSTHOG_COOKIE.exec(name);
		if (!match || match[1] === keepApiKey) continue;
		const expired = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
		document.cookie = expired;
		for (const domain of domains) document.cookie = `${expired}; domain=${domain}`;
	}
}

/**
 * Switches the browser PostHog project: new isolated named PostHog instance per API key
 * (posthog.init's third `name` argument creates a distinct instance instead of mutating the
 * default singleton — the PostHog equivalent of Amplitude's `createInstance()`).
 * Returns whether this invocation ended as the active client (false if superseded).
 */
export async function switchPosthogProject(config: PosthogCommunityConfig): Promise<boolean> {
	const op = ++latestSwitchOp;

	if (typeof window === 'undefined') {
		return false;
	}

	const prev = activeClient;
	activeClient = null;
	disposeClient(prev);

	const networkedKey = (readCrossFrameworkEnv('POSTHOG_KEY') || '').trim() || undefined;
	clearCommunityPosthogCookies(networkedKey);
	// Test-data guard (2026-10-28): Networked's own project on a build that is not production always
	// means the QA project, whatever key the build carries. A community's own project is left alone.
	if (isNetworkedProject(config.apiKey) && analyticsEnvironment() !== 'production') {
		config = { ...config, apiKey: POSTHOG_QA_PROJECT_KEY };
	}

	const instanceName = `networked_${++instanceCounter}`;
	let client: PostHog | null = null;
	try {
		client =
			(posthog.init(
				config.apiKey,
				{
					api_host: config.apiHost || DEFAULT_POSTHOG_HOST,
					// Community projects: no cookie (see clearCommunityPosthogCookies). Networked's
					// own project keeps the default, cookie included, for the website-to-sign-up link.
					...(isNetworkedProject(config.apiKey) ? {} : { persistence: 'localStorage' as const }),
					autocapture: true,
					// Every SPA route change is a $pageview, not just the first load (catalog row 1).
					capture_pageview: 'history_change',
					// Hide by default (D9): every text node is masked unless it sits in a ph-unmask container
					// (header, sidebar, admin rail); media never records. See replayMasking.ts.
					session_recording: {
						maskAllInputs: true,
						maskTextSelector: '*',
						maskTextFn: maskReplayText,
						blockSelector: REPLAY_BLOCK_SELECTOR,
					},
				},
				instanceName
			) as PostHog | undefined) ?? null;
	} catch {
		client = null;
	}

	if (!client) {
		return false;
	}

	if (op !== latestSwitchOp) {
		disposeClient(client);
		return false;
	}

	activeClient = client;
	registerTenantContext(client);
	return true;
}

export function clearPosthogProject(): void {
	latestSwitchOp += 1;
	const prev = activeClient;
	activeClient = null;
	disposeClient(prev);
}

export function getLatestSwitchOp(): number {
	return latestSwitchOp;
}
